/** Target max size for workspace / Cloudinary photo uploads (50 KB). */
export const MAX_PHOTO_BYTES = 50 * 1024;

/** Approximate decoded byte size of a base64 data URL. */
export function estimateDataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const padding = (b64.match(/=+$/) || [''])[0].length;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read the selected image.'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode the image.'));
    img.src = src;
  });
}

function renderJpeg(img: HTMLImageElement, maxDim: number, quality: number): string {
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height, 1));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported in this browser.');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Compress an image file to a JPEG data URL under `maxBytes` (default 50 KB).
 * Uses binary search on JPEG quality first (keeps resolution sharp), then steps
 * down dimensions only if needed.
 */
export async function fileToCompressedDataUrl(
  file: File,
  maxBytes = MAX_PHOTO_BYTES,
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    return readFileAsDataUrl(file);
  }

  const raw = await readFileAsDataUrl(file);
  const img = await loadImage(raw);

  const dims = [1024, 800, 640, 520, 420, 340, 280, 220, 180];

  for (const maxDim of dims) {
    let low = 0.32;
    let high = 0.92;
    let best = renderJpeg(img, maxDim, low);

    for (let i = 0; i < 10; i++) {
      const mid = (low + high) / 2;
      const candidate = renderJpeg(img, maxDim, mid);
      if (estimateDataUrlBytes(candidate) <= maxBytes) {
        best = candidate;
        low = mid;
      } else {
        high = mid;
      }
    }

    if (estimateDataUrlBytes(best) <= maxBytes) {
      return best;
    }
  }

  return renderJpeg(img, 160, 0.3);
}

/** Human-readable size label for UI feedback, e.g. "42 KB". */
export function formatBytesShort(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
}
